<template>
  <lb-dialog
    :value="value"
    :heading="heading"
    width="800"
    @input="$emit('input', $event)"
  >
    <template v-slot:body>
      <form
        autocomplete="off"
        class="fc-notification-form cycle-automation-dialog-form"
        @submit.prevent
      >
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Name</span>
          <lb-string v-model="form.name" label="" data-testid="cycle-digest-form__name" />
        </div>
        <div class="fc-notification-form__field">
          <span class="fc-notification-form__label">Start date <span class="req">*</span></span>
          <lb-date
            v-model="form.startDate"
            label=""
            label-as-hint
            placeholder="Select date"
            :min="$nova.formatDateISO(new Date())"
            :error="errors.startDate ? [errors.startDate] : []"
            @change="onStartDateChange"
            data-testid="cycle-digest-form__start-date"
          />
        </div>
        <div class="fc-notification-form__field">
          <span class="fc-notification-form__label">End date <span class="req">*</span></span>
          <lb-date
            v-model="form.endDate"
            label=""
            label-as-hint
            placeholder="Select date"
            :min="form.startDate"
            :error="errors.endDate ? [errors.endDate] : []"
            @change="errors.endDate = ''"
            data-testid="cycle-digest-form__end-date"
          />
        </div>
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Recipients <span class="req">*</span></span>
          <lb-string
            v-model="form.emails"
            label=""
            placeholder="Separate multiple emails with commas"
            :error="errors.emails ? [errors.emails] : []"
            @input="onEmailsInput"
            data-testid="cycle-digest-form__emails"
          />
        </div>
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Email subject <span class="req">*</span></span>
          <lb-string
            v-model="form.subject"
            label=""
            :error="errors.subject ? [errors.subject] : []"
            @input="errors.subject = ''"
            data-testid="cycle-digest-form__subject"
          />
        </div>
        <div class="fc-notification-form__field fc-notification-form__span-2">
          <span class="fc-notification-form__label">Email body <span class="req">*</span></span>
          <lb-textarea
            v-model="form.mailBody"
            label=""
            :error="errors.mailBody ? [errors.mailBody] : []"
            counter-suffix="characters"
            @input="errors.mailBody = ''"
            data-testid="cycle-digest-form__body"
          />
        </div>
        <div
          class="fc-notification-form__field fc-notification-form__span-2 fc-notification-form__field--chip-wrap cycle-automation-dialog-form__field--chip-wrap"
        >
          <span class="fc-notification-form__label">Locations <span class="req">*</span></span>
          <ChipMultiSelect
            v-model="form.locations"
            outlined
            placeholder=""
            :items="locationItems"
            item-text="name"
            item-value="_id"
            :error="errors.locations"
            :disabled="saving"
            @input="errors.locations = ''"
            data-testid="cycle-digest-form__locations"
            data-testid-prefix="cycle-digest-form__locations"
          />
        </div>
        <div class="fc-notification-form__field">
          <ScheduledAtField
            v-model="form.time"
            label="Sends at"
            compact-timezone
            :error="errors.time"
            data-testid="cycle-digest-form__hour"
            @input="onTimeChange"
          />
        </div>
      </form>
    </template>
    <template #actions>
      <span class="fc-notification-notice">{{ scheduleNotice }}</span>
      <v-spacer />
      <v-btn
        color="primary"
        class="text-none"
        :loading="saving"
        data-testid="cycle-digest-form__save"
        @click="save"
      >
        {{ form._id ? 'Update' : 'Create' }}
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ChipMultiSelect from '../../../../../components/ChipMultiSelect/ChipMultiSelect.vue';
import { ScheduledAtField } from '../../../../../components/Notifications';
import { CRON, EMAIL_NOTIFICATION, SCHEDULE_CHANGE_NOTICE } from '../../../../../utils/constants';
import {
  localStartTimeFromSchedule,
  localTimeFromLegacyHour,
  utcHourTokenFromLocalTime,
} from '../../../../../utils/notificationScheduleUtils';
import { formatEmailList, parseEmailList, validateEmailListField } from '../../../../../utils/validationUtils';

const EMPTY_ERRORS = () => ({
  startDate: '',
  endDate: '',
  emails: '',
  subject: '',
  mailBody: '',
  locations: '',
  time: '',
});

const EMPTY_FORM = () => ({
  name: '',
  startDate: '',
  endDate: '',
  emails: '',
  subject: '',
  mailBody: '',
  locations: [],
  time: '',
});

export default {
  name: 'DashboardDigestFormDialog',
  components: { ChipMultiSelect, ScheduledAtField },
  props: {
    value: { type: Boolean, default: false },
    notification: { type: Object, default: () => ({}) },
    cycleId: { type: String, default: '' },
  },
  data() {
    return {
      saving: false,
      form: EMPTY_FORM(),
      errors: EMPTY_ERRORS(),
      scheduleNotice: SCHEDULE_CHANGE_NOTICE,
    };
  },
  computed: {
    ...mapGetters('emailNotifications', { setupLocations: 'fetchLocations' }),
    ...mapGetters('dashboard', ['getActiveLocations']),
    heading() {
      return this.form._id ? 'Edit dashboard digest' : 'New dashboard digest';
    },
    locationItems() {
      const setup = this.setupLocations || [];
      const cycle = this.getActiveLocations || [];
      const raw = setup.length ? setup : cycle;
      return raw.map((loc) => ({
        ...loc,
        _id: loc._id || loc.id,
        name: loc.name,
      }));
    },
  },
  watch: {
    value(open) {
      if (!open) return;
      this.$store.dispatch('emailNotifications/fetchLocations');
      if (this.cycleId) this.getCountryWiseLocations({ cycleId: this.cycleId });
      this.hydrate(this.notification);
    },
  },
  methods: {
    ...mapActions('dashboard', [
      'addEmailNotification',
      'updateEmailNotification',
      'getCountryWiseLocations',
    ]),
    hydrate(item) {
      this.errors = EMPTY_ERRORS();
      if (!item || !item._id) {
        this.form = EMPTY_FORM();
        return;
      }
      const schedulerTiming = item.schedulerTiming ? String(item.schedulerTiming) : '';
      this.form = {
        ...EMPTY_FORM(),
        _id: item._id,
        name: item.name || '',
        subject: item.subject || '',
        mailBody: item.mailBody || '',
        time: (item.meta?.schedule?.startTime
          ? localStartTimeFromSchedule(item.meta.schedule)
          : localTimeFromLegacyHour(CRON.cronFormatToHour(schedulerTiming))) || '',
        startDate: item.meta?.startDate || '',
        endDate: item.meta?.endDate || '',
        emails: formatEmailList(item.meta?.toUsers),
        locations: Array.isArray(item.meta?.location_key)
          ? item.meta.location_key.map((id) => String(id))
          : [],
      };
    },
    onEmailsInput(value) {
      const { error } = validateEmailListField(value !== undefined ? value : this.form.emails, {
        requiredMessage: '',
        invalidMessage: '',
        duplicateMessage: EMAIL_NOTIFICATION.MESSAGES.DUPLICATE_EMAIL,
      });
      this.errors.emails = error || '';
    },
    onStartDateChange() {
      this.errors.startDate = '';
    },
    onTimeChange() {
      this.errors.time = '';
    },
    validate() {
      this.errors = EMPTY_ERRORS();
      let isValid = true;
      const details = this.form;
      const errors = this.errors;
      if (!details.startDate) {
        errors.startDate = 'Start Date is required';
        isValid = false;
      }
      if (!details.endDate) {
        errors.endDate = 'End Date is required';
        isValid = false;
      } else if (details.startDate && new Date(details.endDate) < new Date(details.startDate)) {
        errors.endDate = 'End Date must be after Start Date';
        isValid = false;
      }
      const emailValidation = validateEmailListField(details.emails, {
        duplicateMessage: EMAIL_NOTIFICATION.MESSAGES.DUPLICATE_EMAIL,
      });
      if (emailValidation.error) {
        errors.emails = emailValidation.error;
        isValid = false;
      }
      if (!details.subject || details.subject.trim() === '') {
        errors.subject = 'Email Subject is required';
        isValid = false;
      }
      if (!details.mailBody || details.mailBody.trim() === '') {
        errors.mailBody = 'Email Body is required';
        isValid = false;
      }
      if (details.time === null || details.time === undefined || details.time === '') {
        errors.time = 'Time is required';
        isValid = false;
      }
      if (!details.locations || details.locations.length === 0) {
        errors.locations = 'At least one location is required';
        isValid = false;
      }
      if (!isValid) {
        const snackbar = errors.emails === EMAIL_NOTIFICATION.MESSAGES.DUPLICATE_EMAIL
          ? EMAIL_NOTIFICATION.MESSAGES.DUPLICATE_EMAIL
          : EMAIL_NOTIFICATION.MESSAGES.REQUIRED_VALIDATION;
        this.$store.commit('sbError', snackbar);
      }
      return isValid;
    },
    async save() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        const notificationData = {
          _id: this.form._id,
          name: this.form.name,
          subject: this.form.subject,
          mailBody: this.form.mailBody,
          time: utcHourTokenFromLocalTime(this.form.time),
          startTime: this.form.time,
          locations: this.form.locations,
          emails: parseEmailList(this.form.emails),
          startDate: this.form.startDate,
          endDate: this.form.endDate,
        };
        const cycleId = this.cycleId;
        const result = notificationData._id
          ? await this.updateEmailNotification({ cycleId, notificationData })
          : await this.addEmailNotification({ cycleId, notificationData });
        if (result && result.success === false) return;
        this.$emit('saved');
        this.$emit('input', false);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../../../../components/Notifications/notificationLayout.scss';
@import '../../../../../components/Notifications/cycleEscalationDialogForm.scss';
</style>
